import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import i18n from "i18next";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Dumbbell,
  Gamepad2,
  Gift,
  GraduationCap,
  HeartPulse,
  MessagesSquare,
  PawPrint,
  Plane,
  QrCode,
  Scissors,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Store,
  Ticket,
  Users,
  Utensils,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { universityList } from "@/assets/universityList";
import { config } from "@/config";
import { HERO_LANDING } from "@/config/heroImages";
import { STATIC_SOCIETIES } from "@/data/societies";
import { PARTNER_BUSINESSES } from "@/data/partnerCoupons";
import { groupSocietiesByUniversity } from "@/data/universityLocations";
import type { SocietyGroup } from "@/data/universityLocations";
import UniversityMap from "@/components/UniversityMap";
import { GetPublicSocietiesAPI } from "@/services/api/society/getPublicSocieties";

const FEATURES = [
  {
    icon: Calendar,
    title: "Events & Calendar",
    desc: "Discover campus events, save them to your calendar and never miss out.",
  },
  {
    icon: Users,
    title: "Society Activities",
    desc: "Join activities hosted by campus societies across Hong Kong.",
  },
  {
    icon: Store,
    title: "Business Activities",
    desc: "Join workshops and activities hosted by partner businesses.",
  },
  {
    icon: QrCode,
    title: "QR Check-in",
    desc: "Check in instantly with a real-time QR code at every activity.",
  },
  {
    icon: Ticket,
    title: "Coupons & Redeem",
    desc: "Earn points and redeem them for coupons at partner shops.",
  },
  {
    icon: MessagesSquare,
    title: "Chat with societies",
    desc: "Message organizers and societies in real time and stay in the loop.",
  },
];

const COUPON_TYPES = [
  { key: "Eat", icon: Utensils },
  { key: "Play", icon: Gamepad2 },
  { key: "Buy", icon: ShoppingBag },
  { key: "Beauty", icon: Sparkles },
  { key: "Fashion", icon: Scissors },
  { key: "Fitness", icon: Dumbbell },
  { key: "Travel", icon: Plane },
  { key: "Wellness", icon: HeartPulse },
  { key: "Education", icon: BookOpen },
  { key: "Pets", icon: PawPrint },
  { key: "Coffee", icon: Coffee },
  { key: "Gifts", icon: Gift },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Create account",
    desc: "Sign up with your verified university email in under a minute.",
  },
  {
    step: "02",
    title: "Browse events",
    desc: "Explore events, coupons and societies across Hong Kong.",
  },
  {
    step: "03",
    title: "Join & subscribe",
    desc: "Follow societies and subscribe to the events you care about.",
  },
  {
    step: "04",
    title: "Check-in & explore",
    desc: "Check in with your QR code and explore everything your campus offers.",
  },
];

const WHY_CHOOSE_US = [
  { icon: ShieldCheck, text: "Verified university email" },
  { icon: BadgeCheck, text: "Easy join" },
  { icon: Sparkles, text: "Free access" },
  { icon: QrCode, text: "Realtime QR check-in" },
  { icon: GraduationCap, text: "Multi-university" },
  { icon: Users, text: "Flexible" },
];

const TESTIMONIALS = [
  {
    quote: "Uninfo makes it so easy to find events happening on campus. I joined three societies in my first month!",
    name: "Emily, HKU",
  },
  {
    quote: "The QR check-in is super smooth. No more queuing to sign in at activities.",
    name: "Jason, HKUST",
  },
  {
    quote: "Finally one platform for every university. I can follow friends at other schools too.",
    name: "Sophie, CUHK",
  },
];

const FALLBACK_SOCIETY_GROUPS = groupSocietiesByUniversity(STATIC_SOCIETIES);

const BusinessIcon: React.FC<{
  icon?: string | null;
  name: string;
  className?: string;
}> = ({ icon, name, className = "h-10 w-10 text-sm" }) => {
  const [failed, setFailed] = useState(false);
  if (!icon || failed) {
    return (
      <span
        className={`flex shrink-0 items-center justify-center rounded-full bg-[#ffa217] font-bold text-white shadow-sm ring-2 ring-white/80 ${className}`}
      >
        {name.charAt(0)}
      </span>
    );
  }
  return (
    <img
      src={icon}
      alt={name}
      title={name}
      className={`shrink-0 rounded-full bg-white object-cover shadow-sm ring-2 ring-white/80 ${className}`}
      onError={() => setFailed(true)}
    />
  );
};

type RecentEvent = {
  id: string;
  title: string;
  image: string | null;
  organizer: string | null;
  university: string | null; // display label, e.g. "University of Hong Kong"
  time: string | null; // formatted from start_date
};

/**
 * Normalize a `/user/events/getEvents` row into a display object for the
 * hero carousel. `event_images` may be `[{ url }]` (IG sync) or legacy
 * plain URL strings.
 */
const normalizeEvent = (item: any): RecentEvent => {
  const details = item?.details ?? {};
  const rawImage = details.event_images?.[0];
  const image =
    typeof rawImage === "string"
      ? rawImage
      : rawImage && typeof rawImage.url === "string"
        ? rawImage.url
        : null;
  // Upgrade http:// to https:// so images aren't blocked as mixed content on secure pages.
  const imageUrl = image?.startsWith("http://") ? `https://${image.slice("http://".length)}` : image;
  // Route Instagram-CDN images through the backend proxy so expiring signed
  // URLs aren't hotlink/cookie-blocked in real browsers.
  let proxiedImage = imageUrl;
  if (proxiedImage) {
    try {
      if (new URL(proxiedImage).hostname.includes("instagram")) {
        proxiedImage = `${config.API_URL}/api/media/image?url=${encodeURIComponent(proxiedImage)}`;
      }
    } catch {
      // Not a valid URL — leave as-is.
    }
  }
  const title = details.title || "Untitled";
  // Without `user_id` the API returns raw `organizer` rows (name/instagram_username);
  // with `user_id` it returns formatted `organizers` (name_en/instagram_username).
  const org = item?.organizers?.[0] ?? item?.organizer?.[0];
  const organizer = org?.name_en || org?.name || org?.instagram_username || null;
  const uniCode = details.university?.[0];
  const university =
    universityList.find((u) => u.value === uniCode)?.label ?? null;
  let time: string | null = null;
  if (details.start_date) {
    const date = new Date(details.start_date);
    if (!Number.isNaN(date.getTime())) {
      time = new Intl.DateTimeFormat("en", {
        weekday: "short",
        hour: "numeric",
        minute: "2-digit",
      }).format(date);
    }
  }
  return {
    id: details.id ?? "",
    title,
    image: proxiedImage,
    organizer,
    university,
    time,
  };
};

// Keeps the hero card populated when the backend is unreachable or empty.
const STATIC_FALLBACK_EVENTS: RecentEvent[] = [
  { id: "static-1", title: "Orientation Night 2026", image: null, organizer: null, university: "HKU", time: "Fri, 7:00 PM" },
  { id: "static-2", title: "Robotics Workshop", image: null, organizer: null, university: "HKUST", time: "Sat, 2:00 PM" },
  { id: "static-3", title: "Music Festival", image: null, organizer: null, university: "CUHK", time: "Sun, 6:30 PM" },
];

const EventCardImage: React.FC<{ event: RecentEvent }> = ({ event }) => {
  const [failed, setFailed] = useState(false);
  if (!event.image || failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#ffa217] to-[#ff8c00]">
        <Calendar size={40} className="text-white/80" />
      </div>
    );
  }
  return (
    <img
      src={event.image}
      alt={event.title}
      referrerPolicy="no-referrer"
      loading="lazy"
      decoding="async"
      className="block h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Circular coverflow carousel for the hero "Upcoming Events" card: the
 * active event sits centered while neighbours peek smaller behind it.
 */
const RecentEventsCarousel: React.FC<{ events: RecentEvent[] }> = ({ events }) => {
  const [index, setIndex] = useState(0);

  if (events.length === 0) return null;

  const go = (offset: number) => {
    setIndex((prev) => (prev + offset + events.length) % events.length);
  };

  const offsets = [-2, -1, 0, 1, 2];

  return (
    <div>
      <div className="relative mt-5 h-80 overflow-hidden">
        {offsets.map((k) => {
          const event = events[(index + k + events.length) % events.length];
          const subtitle = [event.organizer, event.university, event.time].filter(Boolean).join(" · ");
          const positionClass =
            k === 0
              ? "z-20 scale-100 translate-x-0 opacity-100"
              : k === -1
                ? "z-10 -translate-x-24 scale-90 opacity-60"
                : k === 1
                  ? "z-10 translate-x-24 scale-90 opacity-60"
                  : k === -2
                    ? "z-[5] -translate-x-36 scale-75 opacity-30"
                    : "z-[5] translate-x-36 scale-75 opacity-30";
          return (
            <div
              key={k}
              className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${positionClass} ${k === 0 ? "" : "pointer-events-none"}`}
            >
              <div className="relative h-full w-[85%] overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/5">
                <EventCardImage key={event.id} event={event} />
                {k !== 0 && <div className="absolute inset-0 bg-black/35" />}
                {k === 0 && (
                  <>
                    <div className="absolute left-3 top-3 z-10 rounded-full bg-[#ffa217]/90 px-2.5 py-1 text-[11px] font-bold text-white">
                      {event.time || event.university}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                      <div className="line-clamp-2 font-bold text-white">{event.title}</div>
                      {subtitle && (
                        <div className="line-clamp-1 text-xs text-gray-100/80">{subtitle}</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={events.length < 2}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#ffa217]/40 bg-[#fff3e0] text-[#ffa217] shadow-sm transition hover:bg-[#ffa217] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous event"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-xs font-semibold tracking-wider text-gray-500">
          {index + 1} / {events.length}
        </span>
        <button
          type="button"
          onClick={() => go(1)}
          disabled={events.length < 2}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#ffa217]/40 bg-[#fff3e0] text-[#ffa217] shadow-sm transition hover:bg-[#ffa217] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next event"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

/**
 * Public marketing homepage mirroring the carepetkids.com structure:
 * hero → student features → partner businesses & coupons → societies →
 * how it works → why choose us → universities → for business →
 * testimonials.
 */
const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [societyGroups, setSocietyGroups] = useState<SocietyGroup[]>(FALLBACK_SOCIETY_GROUPS);
  const [selectedSocietyId, setSelectedSocietyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${config.API_URL}/api/user/events/getEvents`, {
        params: { page: 1 },
        headers: {
          apikey: config.API_KEY,
          appkey: "uninfo_web_landing",
          lang: i18n.language,
        },
        timeout: 8000,
      })
      .then((resp) => {
        if (cancelled) return;
        const result = resp.data?.result;
        if (Array.isArray(result) && result.length > 0) {
          setRecentEvents(result.slice(0, 10).map(normalizeEvent));
        }
      })
      .catch(() => {
        // Backend unreachable — the static fallback keeps the hero populated.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    GetPublicSocietiesAPI().then((societies) => {
      if (cancelled) return;
      if (societies.length > 0) {
        setSocietyGroups(
          groupSocietiesByUniversity(
            societies.map((s) => ({
              id: s.id,
              name: s.name,
              university: s.university,
              memberCount: 0,
              instagramFollowers: null,
              icon: null,
            })),
          ),
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayEvents = recentEvents.length > 0 ? recentEvents : STATIC_FALLBACK_EVENTS;

  return (
    <div>
      {/* Hero */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_LANDING})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-[#ffa217]/20" />
        <div className="relative z-10 mx-auto grid max-w-6xl animate-fade-down items-center gap-10 px-4 py-16 md:grid-cols-2 md:py-24">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-[#ffa217] shadow-sm">
              <Sparkles size={16} />
              {t("Hong Kong's campus life platform")}
            </div>
            <h1 className="mt-5 text-4xl font-extrabold leading-tight text-white md:text-5xl">
              {t("Your university life,")}
              <br />
              <span className="text-[#ffa217]">{t("all in one place.")}</span>
            </h1>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-gray-200">
              {t("Discover events, join societies, chat with organizers and redeem coupons — built for Hong Kong university students.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-6 py-3 font-bold text-white shadow-lg shadow-[#ffa217]/30 transition hover:bg-[#e08f0e]"
              >
                {t("Join as Student")}
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/for-business"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-[#ffa217] px-6 py-3 font-bold text-[#ffa217] transition hover:bg-[#fff3e0]"
              >
                {t("Join as Business")}
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-gray-300 sm:gap-x-6">
              <div>
                <div className="text-2xl font-extrabold text-white">12+</div>
                {t("Universities")}
              </div>
              <div className="h-8 w-px bg-white/30" />
              <div>
                <div className="text-2xl font-extrabold text-white">100+</div>
                {t("Societies")}
              </div>
              <div className="h-8 w-px bg-white/30" />
              <div>
                <div className="text-2xl font-extrabold text-white">Free</div>
                {t("For students")}
              </div>
            </div>
          </div>

          <div className="relative hidden md:block">
            <div className="rounded-3xl bg-white p-8 shadow-2xl">
              <div className="flex items-center justify-between">
                <div className="text-lg font-bold text-[#333]">{t("Upcoming Events")}</div>
                <span className="rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-semibold text-[#ffa217]">
                  {t("This week")}
                </span>
              </div>
              <div className="mt-5 space-y-4">
                <RecentEventsCarousel events={displayEvents} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Student features */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <div className="text-sm font-bold uppercase tracking-widest text-[#ffa217]">
              {t("For Students")}
            </div>
            <h2 className="mt-2 text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("Everything your campus life needs")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-6 md:grid-cols-3">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="rounded-2xl border border-gray-100 p-8 transition hover:-translate-y-1 hover:border-[#ffa217] hover:shadow-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
                  <feature.icon size={26} />
                </span>
                <h3 className="mt-5 text-xl font-bold text-[#333]">{t(feature.title)}</h3>
                <p className="mt-2 leading-relaxed text-gray-600">{t(feature.desc)}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Partner Businesses & Coupons */}
      <section className="bg-[#fff8f0] py-16 md:py-20">
        <Reveal className="mx-auto max-w-6xl px-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ffa217] text-white">
            <Ticket size={26} />
          </div>
          <div className="mt-5 text-sm font-bold uppercase tracking-widest text-[#ffa217]">
            {t("Coupons & Redeem")}
          </div>
          <h2 className="mt-2 text-3xl font-extrabold text-[#333] md:text-4xl">
            {t("Partner Businesses & Coupons")}
          </h2>
          <p className="mt-4 max-w-md leading-relaxed text-gray-600">
            {t("Partner businesses across Hong Kong back their offers through Uninfo coupons — and sponsors fund even more.")}
          </p>
          <div className="mt-8">
            <div className="text-sm font-bold text-[#d97706]">{t("Coupon types")}</div>
            <div className="mt-3 overflow-hidden">
              <div className="flex w-max items-center gap-2.5 pr-2.5 animate-marquee-reverse hover:[animation-play-state:paused]">
                {[...COUPON_TYPES, ...COUPON_TYPES].map((type, i) => (
                  <span
                    key={`${type.key}-${i}`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#fff3e0] px-3.5 py-2 text-sm font-semibold text-[#d97706] ring-1 ring-[#ffa217]/10"
                  >
                    <type.icon size={16} />
                    {t(type.key)}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <h3 className="mt-10 text-2xl font-extrabold leading-tight text-[#333]">
            {t("Redeem points for coupons you will love")}
          </h3>
          <p className="mt-3 leading-relaxed text-gray-600">
            {t(
              "Earn points and redeem them at partner shops across Hong Kong — dining, entertainment, shopping, beauty, fashion and fitness.",
            )}
          </p>
          <div className="mt-10">
            <div className="text-sm font-bold text-[#d97706]">
              {t("Partner businesses")}
            </div>
            <div className="mt-3 overflow-hidden">
              <div className="flex w-max items-center gap-4 pr-4 animate-marquee hover:[animation-play-state:paused]">
                {[...PARTNER_BUSINESSES, ...PARTNER_BUSINESSES].map((b, i) => (
                  <BusinessIcon
                    key={`${b.id}-${i}`}
                    icon={b.icon}
                    name={b.name}
                    className="h-10 w-10 sm:h-11 sm:w-11"
                  />
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Societies */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <div className="text-sm font-bold uppercase tracking-widest text-[#ffa217]">
              {t("Partners")}
            </div>
            <h2 className="mt-2 text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("Societies we sponsor and partner with")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-gray-600">
              {t("Uninfo HK partners with campus societies to bring their events, updates and everything happening on your campus to you.")}
            </p>
          </div>
          <Reveal className="mt-12 grid items-start gap-8 md:grid-cols-2 lg:grid-cols-5">
            {/* min-w-0 keeps the single mobile column from sizing to the map /
                society names' max-content (long names would otherwise widen
                the track past the viewport and break the page horizontally). */}
            <div className="min-w-0 lg:col-span-3">
              <UniversityMap groups={societyGroups} selectedSocietyId={selectedSocietyId} />
            </div>
            <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-2">
              <div className="text-sm font-bold text-[#d97706]">{t("Societies")}</div>
              <div className="mt-4 max-h-[420px] space-y-6 overflow-y-auto pr-1">
                {societyGroups.map((group) => {
                  if (!group.location) return null;
                  return (
                    <div key={group.location.code}>
                      <h3 className="truncate text-sm font-bold text-[#333]">
                        {t(group.location.code)}
                      </h3>
                      <ul className="mt-2 space-y-0.5">
                        {group.items.map((society) => {
                          const isSelected = society.id === selectedSocietyId;
                          return (
                            <li key={society.id}>
                              <button
                                type="button"
                                onClick={() => setSelectedSocietyId(isSelected ? null : society.id)}
                                aria-pressed={isSelected}
                                className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition ${
                                  isSelected
                                    ? "bg-[#fff3e0] font-semibold text-[#333]"
                                    : "text-gray-600 hover:bg-[#fff3e0] hover:text-[#333]"
                                }`}
                              >
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ffa217]" />
                                <span className="truncate">{society.name}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#1f2937] py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <div className="text-sm font-bold uppercase tracking-widest text-[#ffa217]">
              {t("How It Works")}
            </div>
            <h2 className="mt-2 text-3xl font-extrabold text-white md:text-4xl">
              {t("Get started in four simple steps")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-6 md:grid-cols-4">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className="rounded-2xl bg-white/5 p-6">
                <div className="text-3xl font-extrabold text-[#ffa217]">{item.step}</div>
                <h3 className="mt-3 text-lg font-bold text-white">{t(item.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{t(item.desc)}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <div className="text-sm font-bold uppercase tracking-widest text-[#ffa217]">
              {t("Why Choose Us")}
            </div>
            <h2 className="mt-2 text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("Built for Hong Kong students")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {WHY_CHOOSE_US.map((item) => (
              <div
                key={item.text}
                className="flex items-center gap-4 rounded-2xl border border-gray-100 p-5 transition hover:border-[#ffa217]"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#fff3e0] text-[#ffa217]">
                  <item.icon size={22} />
                </span>
                <div className="font-semibold text-[#333]">{t(item.text)}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Universities served */}
      <section className="bg-[#fff8f0] py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <div className="text-sm font-bold uppercase tracking-widest text-[#ffa217]">
              {t("Universities")}
            </div>
            <h2 className="mt-2 text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("Serving all major Hong Kong universities")}
            </h2>
          </div>
          <Reveal className="mt-10 grid grid-cols-2 items-center gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4">
            {universityList.map((uni) => (
              <div
                key={uni.value}
                className="flex h-full items-center gap-2.5 rounded-2xl border border-gray-100 bg-white p-3 transition hover:border-[#ffa217] sm:gap-3 sm:p-4"
              >
                <img
                  src={uni.image}
                  alt={t(uni.value)}
                  className="h-9 w-9 shrink-0 rounded object-contain"
                />
                <div className="min-w-0 break-words text-sm font-semibold leading-snug text-[#333]">
                  {t(uni.value)}
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* For Business */}
      <section className="bg-gradient-to-r from-[#ffa217] to-[#ff8c00] py-16">
        <Reveal className="mx-auto max-w-6xl px-4">
          <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
            <div>
              <div className="text-sm font-bold uppercase tracking-widest text-white/80">
                {t("For Business")}
              </div>
              <h2 className="mt-1 text-3xl font-extrabold text-white">
                {t("Are you a business or society?")}
              </h2>
              <p className="mt-2 text-white/90">
                {t("Reach thousands of verified university students across Hong Kong.")}
              </p>
              <p className="mt-1 text-sm text-white/80">
                {t("Join our partner network to put your brand in front of thousands of verified students.")}
              </p>
            </div>
            <Link
              to="/for-business"
              className="shrink-0 rounded-xl bg-white px-8 py-3 font-bold text-[#ffa217] shadow-lg transition hover:bg-[#fff3e0]"
            >
              {t("Join as Business")}
            </Link>
          </div>

          {/* Partnership reach */}
          <div className="mt-10 grid grid-cols-3 gap-2.5 rounded-2xl bg-white/10 p-4 text-center sm:gap-4 sm:p-6">
            <div>
              <div className="text-2xl font-extrabold text-white sm:text-3xl">12+</div>
              <div className="mt-1 text-xs text-white/80 sm:text-sm">{t("Universities")}</div>
            </div>
            <div>
              <div className="text-2xl font-extrabold text-white sm:text-3xl">100+</div>
              <div className="mt-1 text-xs text-white/80 sm:text-sm">{t("Societies")}</div>
            </div>
            <div>
              <div className="text-2xl font-extrabold text-white sm:text-3xl">10k+</div>
              <div className="mt-1 text-xs text-white/80 sm:text-sm">{t("Students")}</div>
            </div>
          </div>

        </Reveal>
      </section>

      {/* Testimonials */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <div className="text-sm font-bold uppercase tracking-widest text-[#ffa217]">
              {t("Testimonials")}
            </div>
            <h2 className="mt-2 text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("Loved by students")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((item) => (
              <div key={item.name} className="rounded-2xl border border-gray-100 p-8">
                <div className="text-4xl text-[#ffa217]">"</div>
                <p className="mt-2 leading-relaxed text-gray-700">{item.quote}</p>
                <div className="mt-5 font-bold text-[#333]">{item.name}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;
